import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useC358Query } from './hooks/useC358Query';
import type { ReportRow, CurrencySummary } from '../../services/c358Api';
import './C358Form.css';

const C358Form: React.FC = () => {
  const { loading, data, currencySummary, groupCounts, queried, noData, runQuery, reset } = useC358Query();

  const [reportType, setReportType] = useState<string>('detail');
  const [dataSource, setDataSource] = useState<string>('bulk');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [scNoFrom, setScNoFrom] = useState('');
  const [scNoTo, setScNoTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  const [customerFrom, setCustomerFrom] = useState('');
  const [customerTo, setCustomerTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType, data_source: dataSource,
      date_from: dateFrom.trim() || undefined, date_to: dateTo.trim() || undefined,
      sc_no_from: scNoFrom.trim() || undefined, sc_no_to: scNoTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined, dept_to: deptTo.trim() || undefined,
      sales_from: salesFrom.trim() || undefined, sales_to: salesTo.trim() || undefined,
      customer_from: customerFrom.trim() || undefined, customer_to: customerTo.trim() || undefined,
      product_from: productFrom.trim() || undefined, product_to: productTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('detail'); setDataSource('bulk');
    setDateFrom(''); setDateTo('');
    setScNoFrom(''); setScNoTo('');
    setDeptFrom(''); setDeptTo('');
    setSalesFrom(''); setSalesTo('');
    setCustomerFrom(''); setCustomerTo('');
    setProductFrom(''); setProductTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>S/C受訂統計排行表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:10px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 4px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:8mm}}</style></head><body>
<h2>S/C受訂統計排行表</h2>
<table><thead><tr><th>部門</th><th>業務員</th><th>S/C號</th><th>S/C日期</th><th>幣別</th><th>原幣金額</th><th>本幣金額(A)</th><th>採購金額</th><th>淨利(C)</th><th>淨利率%</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.dept_name || r.FC110_10}</td><td>${r.sales_name || r.FC110_13}</td><td>${r.FC110_01}</td><td>${r.FC110_11}</td><td>${r.FC110_24}</td><td class="right">${r.FC111_16?.toFixed(2)}</td><td class="right">${r.sell_gross?.toFixed(0)}</td><td class="right">${r.FC111_37?.toFixed(0)}</td><td class="right">${r.profit?.toFixed(0)}</td><td class="right">${r.profit_pct?.toFixed(2)}</td></tr>`).join('')}
</tbody></table><p>共 ${data.length} 筆</p></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const baseColumns: ColumnsType<ReportRow> = [
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 100, render: (v: string, r: ReportRow) => v || r.FC110_10 },
    { title: '業務員', dataIndex: 'sales_name', key: 'sales_name', width: 80, render: (v: string, r: ReportRow) => v || r.FC110_13 },
    { title: '指定客戶', dataIndex: 'FC110_59', key: 'FC110_59', width: 100, ellipsis: true },
    { title: 'S/C號碼', dataIndex: 'FC110_01', key: 'FC110_01', width: 100 },
    { title: 'S/C日期', dataIndex: 'FC110_11', key: 'FC110_11', width: 95 },
    { title: '幣別', dataIndex: 'FC110_24', key: 'FC110_24', width: 55 },
    { title: '原幣金額', dataIndex: 'FC111_16', key: 'FC111_16', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '本幣金額(A)', dataIndex: 'sell_gross', key: 'sell_gross', width: 110, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '採購金額', dataIndex: 'FC111_37', key: 'FC111_37', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '毛利(B)', dataIndex: 'profit_yg', key: 'profit_yg', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '淨利(C)', dataIndex: 'profit', key: 'profit', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '毛利率%', dataIndex: 'profit_yg_pct', key: 'profit_yg_pct', width: 80, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '淨利率%', dataIndex: 'profit_pct', key: 'profit_pct', width: 80, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const detailColumns: ColumnsType<ReportRow> = [
    ...baseColumns.slice(0, 5),
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 120 },
    { title: '單位', dataIndex: 'FC111_14', key: 'FC111_14', width: 55 },
    { title: '數量', dataIndex: 'FC111_13', key: 'FC111_13', width: 80, align: 'right' as const },
    ...baseColumns.slice(5),
  ];

  const columns = reportType === 'detail' ? detailColumns : baseColumns;

  const summaryColumns: ColumnsType<CurrencySummary> = [
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: '原幣合計', dataIndex: 'total_fgn_amt', key: 'total_fgn_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '本幣合計', dataIndex: 'total_sell', key: 'total_sell', align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '採購合計', dataIndex: 'total_purchase', key: 'total_purchase', align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '淨利合計', dataIndex: 'total_profit', key: 'total_profit', align: 'right' as const, render: (v: number) => v?.toFixed(0) },
    { title: '平均利率%', dataIndex: 'avg_margin', key: 'avg_margin', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.C358} action="query">
      <div className="c358-container">
        <div className="c358-header">
          <h2>S/C受訂統計排行表</h2>
          {queried && !noData && <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>{filterLabel('報表類型')}<Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}><Radio.Button value="detail">明細</Radio.Button><Radio.Button value="summary">彙總</Radio.Button></Radio.Group></div>
            <div>{filterLabel('資料來源')}<Radio.Group value={dataSource} onChange={e => setDataSource(e.target.value)}><Radio.Button value="bulk">大貨</Radio.Button><Radio.Button value="sample_sale">銷售樣</Radio.Button><Radio.Button value="all">合併</Radio.Button><Radio.Button value="sample_order">樣品單</Radio.Button></Radio.Group></div>
          </div>
          <Space wrap size="middle">
            <div>{filterLabel('S/C日期起')}<Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 130 }} allowClear /></div>
            <div>{filterLabel('S/C日期迄')}<Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 130 }} allowClear /></div>
            <div>{filterLabel('S/C號碼起')}<Input value={scNoFrom} onChange={e => setScNoFrom(e.target.value)} placeholder="起" style={{ width: 110 }} allowClear /></div>
            <div>{filterLabel('S/C號碼迄')}<Input value={scNoTo} onChange={e => setScNoTo(e.target.value)} placeholder="迄" style={{ width: 110 }} allowClear /></div>
            <div>{filterLabel('部門起')}<Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('部門迄')}<Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('業務員起')}<Input value={salesFrom} onChange={e => setSalesFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('業務員迄')}<Input value={salesTo} onChange={e => setSalesTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('客戶起')}<Input value={customerFrom} onChange={e => setCustomerFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('客戶迄')}<Input value={customerTo} onChange={e => setCustomerTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('產品起')}<Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 100 }} allowClear /></div>
            <div>{filterLabel('產品迄')}<Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 100 }} allowClear /></div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space><Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button><Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button></Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                部門筆數：{groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns} dataSource={data}
              rowKey={(r, i) => `${r.FC110_01}-${r.product_code}-${i}`}
              loading={loading} size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: reportType === 'detail' ? 1500 : 1200 }}
              style={{ marginBottom: 16 }}
            />
            {currencySummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>幣別合計</div>
                <Table<CurrencySummary>
                  columns={summaryColumns} dataSource={currencySummary}
                  rowKey="currency" size="small" pagination={false} scroll={{ x: 600 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default C358Form;
